/**
 * send_to_friend.js
 * Alpine factory for the send-to-friend form.
 * Auto-bundled by Vite — do NOT import manually.
 *
 * Data contract (emitted inline by send_to_friend.jinja):
 *   <script type="application/json" data-send-to-friend-config>
 *     { fields:[{name,required,type,label}], hasHcaptcha, hcaptchaSiteKey,
 *       hcaptchaSize, hcaptchaContainerId, successMessage, errorMessage }
 *   </script>
 *   Icon stubs: #stf-icon-check, #stf-icon-error (hidden spans)
 */

function sendToFriendForm() {
    let cfg = {};
    let fields = [];

    function readConfig(root) {
        const script = root?.querySelector?.('[data-send-to-friend-config]')
            || document.querySelector('[data-send-to-friend-config]');
        try { cfg = JSON.parse(script?.textContent || '{}'); } catch (e) { cfg = {}; }
        fields = cfg.fields || [];
    }

    function iconHtml(id) {
        return document.getElementById(id)?.innerHTML || '';
    }

    function alertHtml(type, body) {
        const icon = iconHtml(type === 'success' ? 'stf-icon-check' : 'stf-icon-error');
        const color = type === 'success' ? 'success' : 'error';
        return `<div class="mb-4 p-4 bg-${color}/10 border border-${color}/20 rounded-lg">
            <div class="flex items-start">
                <span class="icon text-${color} w-5 h-5 mt-0.5 mr-3">${icon}</span>
                <div>${body}</div>
            </div>
        </div>`;
    }

    function showMsg(id, html) {
        const el = document.getElementById(id);
        if (!el) return;
        el.classList.remove('hidden');
        el.style.display = 'block';
        el.innerHTML = html;
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }

    function responsePayload(event) {
        try { return JSON.parse(event.detail.xhr?.responseText || '{}'); } catch (e) { return {}; }
    }

    // Build initial formData from field config
    const initialData = {};
    fields.forEach(f => { initialData[f.name] = ''; });

    return {
        formData: { ...initialData },
        errors: {},
        isSubmitting: false,
        isFormValid: false,
        hcaptchaCompleted: !cfg.hasHcaptcha,
        hcaptchaWidgetId: null,

        closeModal() {
            const content = this.$refs.shareForm?.closest('[id^="htmx-modal-content-"]');
            const modalId = content?.id.replace('htmx-modal-content-', '');
            const triggerRoot = modalId ? document.getElementById(`modal-trigger-${modalId}`)?.closest('.mo-root') : null;
            const alpine = triggerRoot?._x_dataStack?.[0];
            if (alpine && typeof alpine.handleClose === 'function') alpine.handleClose();
            else window.dispatchEvent(new CustomEvent('close-modal'));
        },

        captchaResponseField() {
            return this.$refs.shareForm?.querySelector('[name="jinja_data.output_doc_payload.h-captcha-response"]')
                || document.getElementById('hcaptcha-response');
        },

        modalItemPayload() {
            const content = this.$refs.shareForm?.closest('[id^="htmx-modal-content-"]');
            if (!content) return {};
            const modalId = content.id.replace('htmx-modal-content-', '');
            const script = document.getElementById(`modal-data-${modalId}-json`);
            try { return JSON.parse(script?.textContent || '{}')?.jinja_data || {}; } catch (e) { return {}; }
        },

        populateItemPayload() {
            const payload = this.modalItemPayload();
            ['item_id', 'item_title', 'item_reference', 'item_image'].forEach(key => {
                const input = this.$refs.shareForm?.querySelector(`[name="jinja_data.output_doc_payload.${key}"]`);
                if (input && !input.value && payload[key]) input.value = payload[key];
            });
        },

        getHcaptchaTheme() {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        },

        loadHcaptcha() {
            if (!cfg.hasHcaptcha || window.hcaptchaScriptLoaded) return;
            window.hcaptchaScriptLoaded = true;
            const script = document.createElement('script');
            script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
            script.async = true;
            script.defer = true;
            script.onload = () => window.dispatchEvent(new Event('hcaptchaLoaded'));
            document.head.appendChild(script);
        },

        renderHcaptcha() {
            if (!cfg.hasHcaptcha || typeof hcaptcha === 'undefined') return;
            const container = document.getElementById(cfg.hcaptchaContainerId);
            const resp = this.captchaResponseField();
            if (!container || container.querySelector('iframe')) return;
            this.hcaptchaWidgetId = hcaptcha.render(container, {
                sitekey: cfg.hcaptchaSiteKey,
                size: cfg.hcaptchaSize || 'normal',
                theme: this.getHcaptchaTheme(),
                callback: token => {
                    if (resp) resp.value = token;
                    this.hcaptchaCompleted = true;
                    this.checkFormValidity();
                },
                'expired-callback': () => {
                    if (resp) resp.value = '';
                    this.hcaptchaCompleted = false;
                    this.checkFormValidity();
                },
                'error-callback': () => {
                    if (resp) resp.value = '';
                    this.hcaptchaCompleted = false;
                    this.checkFormValidity();
                },
            });
        },

        checkFormValidity() {
            let ok = fields.every(f => !f.required || this.formData[f.name]?.trim());
            if (cfg.hasHcaptcha && !this.hcaptchaCompleted) ok = false;
            this.isFormValid = ok;
            return ok;
        },

        validateForm() {
            this.errors = {};
            let ok = true;
            fields.forEach(f => {
                if (f.required && !this.formData[f.name]?.trim()) {
                    this.errors[f.name] = `${f.label} is required`; ok = false;
                }
                if (f.type === 'email' && this.formData[f.name] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData[f.name])) {
                    this.errors[f.name] = 'Please enter a valid email address'; ok = false;
                }
            });
            return ok;
        },

        submitForm(event) {
            event.preventDefault();
            this.populateItemPayload();
            document.getElementById('share-form-messages').innerHTML = '';
            if (!this.validateForm()) {
                this.showValidationErrors();
                if (cfg.hasHcaptcha) this.resetHcaptcha();
                return false;
            }
            if (cfg.hasHcaptcha && !this.captchaResponseField()?.value) {
                this.showCaptchaError(); return false;
            }
            htmx.trigger(this.$refs.shareForm, 'share-submit');
            return false;
        },

        handleFormResponse(event) {
            const payload = responsePayload(event);
            const ok = event.detail.successful && payload.success !== false;
            if (ok) {
                showMsg('share-form-messages', alertHtml('success',
                    `<p class="text-base font-medium text-success">${payload.message || cfg.successMessage || 'Email sent successfully.'}</p>`));
                setTimeout(() => {
                    const el = document.getElementById('share-form-messages');
                    if (el) { el.innerHTML = ''; el.classList.add('hidden'); el.style.display = 'none'; }
                    this.closeModal();
                    this.resetForm();
                }, 1500);
            } else {
                showMsg('share-form-messages', alertHtml('error',
                    `<p class="text-base font-medium text-error">${payload.message || cfg.errorMessage || 'Failed to send email. Please try again.'}</p>`));
                if (cfg.hasHcaptcha) this.resetHcaptcha();
            }
        },

        showValidationErrors() {
            const list = Object.values(this.errors).map(e => `<li>${e}</li>`).join('');
            showMsg('share-form-messages', alertHtml('error',
                `<p class="text-base font-medium text-error mb-1">Please fix the following errors:</p>
                 <ul class="text-base text-error list-disc list-inside">${list}</ul>`));
        },

        showCaptchaError() {
            showMsg('share-form-messages', alertHtml('error',
                '<p class="text-base font-medium text-error">Please complete the captcha verification</p>'));
        },

        resetHcaptcha() {
            if (!cfg.hasHcaptcha) return;
            setTimeout(() => {
                try {
                    const container = document.getElementById(cfg.hcaptchaContainerId);
                    const resp = this.captchaResponseField();
                    if (resp) resp.value = '';
                    if (typeof hcaptcha !== 'undefined') {
                        try { hcaptcha.reset(); } catch (e) {}
                        if (container) {
                            const wid = container.querySelector('iframe')?.getAttribute('data-hcaptcha-widget-id');
                            if (wid) { try { hcaptcha.reset(wid); } catch (e) {} }
                            container.innerHTML = '';
                            hcaptcha.render(container, {
                                sitekey: cfg.hcaptchaSiteKey,
                                size: cfg.hcaptchaSize || 'normal',
                                theme: this.getHcaptchaTheme(),
                                callback: token => {
                                    this.hcaptchaCompleted = true; this.checkFormValidity();
                                    if (resp) resp.value = token;
                                },
                                'expired-callback': () => { this.hcaptchaCompleted = false; this.checkFormValidity(); },
                            });
                        }
                    }
                    this.hcaptchaCompleted = false;
                    this.checkFormValidity();
                } catch (e) { console.error('Error resetting hCaptcha:', e); }
            }, 100);
        },

        resetForm() {
            this.formData = { ...initialData };
            this.errors = {};
            this.isFormValid = false;
            if (cfg.hasHcaptcha) this.resetHcaptcha();
            const el = document.getElementById('share-form-messages');
            if (el) { el.innerHTML = ''; el.classList.add('hidden'); el.style.display = 'none'; }
        },

        init() {
            readConfig(this.$el);
            Object.keys(initialData).forEach(key => { delete initialData[key]; });
            fields.forEach(f => { initialData[f.name] = ''; });
            this.formData = { ...initialData };
            this.hcaptchaCompleted = !cfg.hasHcaptcha;
            this.checkFormValidity();
            this.populateItemPayload();
            fields.forEach(f => this.$watch(`formData.${f.name}`, () => this.checkFormValidity()));

            if (cfg.hasHcaptcha) {
                this.loadHcaptcha();
                if (typeof hcaptcha !== 'undefined') this.renderHcaptcha();
                window.addEventListener('hcaptchaLoaded', () => this.renderHcaptcha());
                window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => this.resetHcaptcha());
                window.addEventListener('hcaptcha-widget-rendered', e => {
                    if (e.detail?.widgetId !== undefined) this.hcaptchaWidgetId = e.detail.widgetId;
                });
            }

            window.addEventListener('open-enquiry-modal', () => {
                this.resetForm();
                setTimeout(() => this.checkFormValidity(), 50);
            });
        },
    };
}

window.sendToFriendForm = sendToFriendForm;
